import { ChevronDown } from 'lucide-react';
import { Children, isValidElement } from 'react';
import type { DetailsHTMLAttributes, ReactNode } from 'react';

/**
 * Стилизованный <summary> с собственным chevron-ом. Браузерный
 * default-triangle скрыт через `[&::-webkit-details-marker]:hidden`.
 */
export function Summary({ children }: { children?: ReactNode }) {
	return (
		<summary className="flex cursor-pointer list-none items-center gap-2 py-3 text-base font-medium text-gray-1 dark:text-white [&::-webkit-details-marker]:hidden">
			<ChevronDown className="h-4 w-4 shrink-0 text-gray-3 transition-transform duration-200 group-open:rotate-180" />
			{children}
		</summary>
	);
}

/**
 * Native <details> с обёрткой контента в стилизованный div.
 * Соседние <details> склеивают общую границу через CSS-селектор
 * `[details+&]:border-t-0`, чтобы они визуально образовывали один блок.
 */
export function Details({
	children,
	...props
}: DetailsHTMLAttributes<HTMLDetailsElement>) {
	const kids = Children.toArray(children);
	const summaryIndex = kids.findIndex(
		(c) => isValidElement(c) && (c.type === Summary || c.type === 'summary'),
	);
	const head = summaryIndex >= 0 ? kids.slice(0, summaryIndex + 1) : [];
	const body = summaryIndex >= 0 ? kids.slice(summaryIndex + 1) : kids;
	const trimmedBody = body.filter(
		(c) => !(typeof c === 'string' && c.trim() === ''),
	);

	return (
		<details
			className="group mt-4 border-b border-t border-gray-4 dark:border-gray-3 [details+&]:mt-0 [details+&]:border-t-0"
			{...props}
		>
			{head}
			{trimmedBody.length > 0 && (
				<div className="ml-6 pb-3 text-base leading-relaxed text-gray-3">
					{trimmedBody}
				</div>
			)}
		</details>
	);
}
